import {DataFrameColumnType} from 'data-frames/DataFrameTypes';
import {
  DataFrameFilterOperation,
  OPERATOR_EQUIVALENCES,
  UserFacingFilterOperation,
} from 'data-frames/filters/DataFrameFilterTypes';
import {LeafType, NUMERICAL_LEAF_TYPES} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {leafTypeHasBenchlingTypeValueInput} from 'data-types/components/BenchlingTypeValueInputUtil';
import {isMultiSupportedType} from 'data-types/components/internals/ValueInputTypes';

/**
 * Get the filter operations that are valid for a column type.
 *
 * This is the intersection of the operations that are supported by the FILTER_DATASET step and
 * the <DataFrameFilterCreator> component.
 */
export function getValidOperationsForColumnType(
  columnType: DataFrameColumnType
): Array<DataFrameFilterOperation> {
  if (columnType.maybeText || !leafTypeHasBenchlingTypeValueInput(columnType.leafType)) {
    return [DataFrameFilterOperation.IS_NULL];
  }

  if (columnType.isList) {
    return [DataFrameFilterOperation.IS_NULL, DataFrameFilterOperation.INCLUDES];
  }

  // All leaf types support IS_NULL and EQUALS operations
  let operations = [DataFrameFilterOperation.IS_NULL, DataFrameFilterOperation.EQUALS];

  if (isMultiSupportedType(columnType.leafType)) {
    operations = [...operations, DataFrameFilterOperation.IS_IN];
  }

  if ([...NUMERICAL_LEAF_TYPES, LeafType.DATE, LeafType.DATETIME].includes(columnType.leafType)) {
    operations = [
      ...operations,
      DataFrameFilterOperation.LESS_THAN,
      DataFrameFilterOperation.LESS_THAN_OR_EQUALS,
      DataFrameFilterOperation.GREATER_THAN,
      DataFrameFilterOperation.GREATER_THAN_OR_EQUALS,
    ];
  }

  if (columnType.leafType === LeafType.TEXT) {
    operations = [
      ...operations,
      DataFrameFilterOperation.CONTAINS,
      DataFrameFilterOperation.STARTS_WITH,
      DataFrameFilterOperation.ENDS_WITH,
    ];
  }

  return operations;
}

/**
 * Get the user-facing filter operations that can be selected a column type.
 */
export function getValidUserFacingOperationsForColumnType(
  columnType: DataFrameColumnType
): Array<UserFacingFilterOperation> {
  const allowedOperations: Set<DataFrameFilterOperation | null> = new Set(
    getValidOperationsForColumnType(columnType)
  );
  return [...OPERATOR_EQUIVALENCES.keys()].filter((userFacingOperation) =>
    allowedOperations.has(OPERATOR_EQUIVALENCES.get(userFacingOperation)?.operation ?? null)
  );
}
